import { Fragment, useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { DateRange } from "react-day-picker";
import useUiuxStore from "src/store/useUiuxStore";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { InputForm } from "src/components/common/form/input/InputForm";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { SearchDateRangeForm } from "src/components/common/form/search/SearchDateRangeForm";
import { formatDate } from "src/utils/string/stringUtil";

import ModalWrapper from "../ModalWrapper";
import { SearchForm } from "../SearchForm";
import { SearchTable } from "./SearchTable";

import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { useCodeSearch } from "src/hooks/useCodeSearch";

import { SearchableDto, CodeType } from "src/types/base";
import { getCodeTypeFromTitle, getSearchFieldsByCodeType } from "src/utils/categoryUtils";
import { getCategoryByType } from "src/configs/categories";
import { useDescText } from "src/hooks/useDescText";
import { DESC_BTN, DESC_FLD } from "src/utils/descLanguage/descLanguageUtil";
import {
  CodeSearchModalProps,
  SearchState,
  SearchAction,
  FocusMoveConfig,
  SelectionEvent,
} from "src/types/code-search";
import { buttonFormHeightOption, defaultFormText } from "../../common";

const searchReducer = (state: SearchState, action: SearchAction): SearchState => {
  switch (action.type) {
    case "SET_FILTERS":
      return { ...state, filters: action.payload };
    case "SET_GRID_DATA":
      return { ...state, gridData: action.payload };
    case "SET_FILTERED_LIST":
      return { ...state, filteredList: action.payload };
    case "SET_SELECTED":
      return { ...state, selected: action.payload };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_ERROR":
      return { ...state, errorMessage: action.payload };
    case "INITIALIZE":
      return { ...state, filters: action.payload.filters };
    case "RESET":
      return {
        filters: {},
        gridData: [],
        filteredList: [],
        selected: undefined,
        isLoading: false,
        errorMessage: undefined,
      };
    default:
      return state;
  }
};

const initialState: SearchState = {
  filters: {},
  gridData: [],
  filteredList: [],
  selected: undefined,
  isLoading: false,
  errorMessage: undefined,
};

const getSearchOptionCommentCd = (key: string): string | undefined => {
  switch (key) {
    case "search":
      return DESC_FLD.SEARCH_TERM;
    case "compCd":
      return DESC_FLD.BUSINESS_SITE;
    case "orderDate":
      return DESC_FLD.WORK_DATE;
    case "productDate":
      return DESC_FLD.PRODUCTION_DATE;
    case "workplaceCd":
      return DESC_FLD.WORK_PROCESS;
    default:
      return undefined;
  }
};

const getCategoryCommentCd = (category?: string): string | undefined => {
  switch (category) {
    case "작업지시번호":
      return DESC_FLD.WORK_ORDER_NO;
    case "생산지시번호":
    case "생산지시":
      return DESC_FLD.PRODUCTION_ORDER_NO;
    case "생산실적":
      return DESC_FLD.PRODUCTION_RESULT;
    case "담당자":
      return DESC_FLD.PERSON_IN_CHARGE;
    case "작업자":
      return DESC_FLD.WORKER;
    default:
      return undefined;
  }
};

const useSearchLogic = (codeSearchModalProps: CodeSearchModalProps | null) => {
  const [state, dispatch] = useReducer(searchReducer, initialState);
  const { setLoadingSpinner } = useLoadingSpinnerStore();
  const { searchForModalByType } = useCodeSearch();


  const currentCodeType = useMemo((): CodeType | null => {
    if (!codeSearchModalProps?.category) return null;
    return getCodeTypeFromTitle(codeSearchModalProps.category) || null;
  }, [codeSearchModalProps?.category]);

  // API 호출 함수 (코드 유형에 따라 다른 API 호출)
  const executeSearch = useCallback(async () => {
    if (!currentCodeType) return;
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: "" });
      setLoadingSpinner(true);
      
      const responseData = await searchForModalByType(
        currentCodeType,
        state.filters["search"] || "",
        state.filters
      );

      const dataArray = Array.isArray(responseData) ? responseData : [];
      dispatch({ type: "SET_GRID_DATA", payload: dataArray });
      dispatch({ type: "SET_FILTERED_LIST", payload: dataArray });
    } catch (error) {
      console.error("Failed to fetch data", error);
      const errorMessage =
        error instanceof Error ? error.message : "데이터를 불러오는데 실패했습니다.";
      dispatch({ type: "SET_ERROR", payload: errorMessage });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
      setLoadingSpinner(false);
    }
  }, [currentCodeType, state.filters, searchForModalByType, setLoadingSpinner]);

  // 클라이언트 필터링 — 현재 카테고리의 searchFields 기준으로 처리
  const applyClientFiltering = useCallback(() => {
    if (!currentCodeType) return;

    const searchTerm = state.filters["search"] || "";
    if (!searchTerm.trim()) {
      dispatch({ type: "SET_FILTERED_LIST", payload: state.gridData });
      return;
    }

    const fields = getSearchFieldsByCodeType(currentCodeType);
    const searchLower = searchTerm.toLowerCase();
    const filtered = state.gridData.filter((item: any) =>
      fields.some((field) => item[field]?.toString().toLowerCase().includes(searchLower))
    );
    dispatch({ type: "SET_FILTERED_LIST", payload: filtered });
  }, [state.gridData, state.filters, currentCodeType]);

  // 초기화 함수 (filters + dateRange UI 상태를 함께 초기화)
	const initializeSearch = useCallback((
		setDateRangesCallback: (ranges: Record<string, DateRange | undefined>) => void,
		clearExtraFilter: boolean = false,
		clearSearch: boolean = false,
	) => {
		if (!codeSearchModalProps) return;

		const codeType = getCodeTypeFromTitle(codeSearchModalProps.category) || null;
		const category = codeType ? getCategoryByType(codeType) : undefined;
		const extraFilter = codeSearchModalProps.extraFilter || {};
		const initialDateRanges: Record<string, DateRange | undefined> = {};

		const defaultFilters: Record<string, string> = {
			search: clearSearch ? '' : (codeSearchModalProps.search || '')
		};
		category?.searchOptions?.forEach(option => {
			if (option.type === 'dropdown' && option.defaultValue !== undefined) {
				defaultFilters[option.key] = option.defaultValue;
			}
			if (option.type === 'dateRange' && option.defaultDateRange === 'currentMonth') {
				const now = new Date();
				const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
				initialDateRanges[option.key] = { from: firstDay, to: now };
				defaultFilters[`${option.key}From`] = formatDate(firstDay);
				defaultFilters[`${option.key}To`] = formatDate(now);
			}
		});

    const processedExtraFilter = Object.fromEntries(
      Object.entries(extraFilter).map(([key, value]) => [key, String(value || "all")])
    );

    setDateRangesCallback(initialDateRanges); // 일자에 대한 기본값을 받도록

    dispatch({
      type: "INITIALIZE",
      payload: {
				filters: {
					...defaultFilters,
					...(clearExtraFilter ? {} : processedExtraFilter),
				},
			},
    });
  }, [codeSearchModalProps]);

  return {
    state,
    dispatch,
    currentCodeType,
    executeSearch,
    applyClientFiltering,
    initializeSearch,
  };
};

// ===== 커스텀 훅: 모달 생명주기 =====
const useModalLifecycle = (codeSearchModalProps: CodeSearchModalProps | null, codeSearchModalVisible: boolean) => {
	// 키보드 이벤트 관리
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => { if (e.key === "Enter") {} };
		if (codeSearchModalVisible) window.addEventListener("keydown", handleKeyDown);
		return () => { window.removeEventListener("keydown", handleKeyDown); };
	}, [codeSearchModalVisible]);

  	// 모달 초기화
	useEffect(() => {
		if (codeSearchModalProps?.category) {}
	}, [codeSearchModalProps?.category]);
};

export const CodeSearchModal = () => {
  const { codeSearchModalProps, codeSearchModalVisible, setCodeSearchModalVisible } = useUiuxStore();
  const commonCodeStore = useCommonCodeStore();
  const { renderDescText, getFormTitleWidth } = useDescText();
	// 코드 검색 팝업은 화면별 분기 없이 동일한 디자인을 사용한다.
	const isKSK2022R01Style = true;

  const { state, dispatch, currentCodeType, executeSearch, applyClientFiltering, initializeSearch }
  = useSearchLogic(codeSearchModalProps || null);
  
  const [dateRanges, setDateRanges] = useState<Record<string, DateRange | undefined>>({});
  const [pickerVisible, setPickerVisible] = useState(false);
	const [pickerPosition, setPickerPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

	// DateRange 객체는 컴포넌트 상태에, API용 문자열은 filters에 반영
	const handleDateRangeChange = useCallback((key: string, range: DateRange | undefined) => {
		setDateRanges((prev) => ({ ...prev, [key]: range }));
		dispatch({
			type: 'SET_FILTERS',
			payload: {
				...state.filters,
				[`${key}From`]: range?.from ? formatDate(range.from) : '',
				[`${key}To`]: range?.to ? formatDate(range.to) : '',
			}
		});
	}, [state.filters]);

// 모달 크기 설정
const categoryModalSize = useMemo(() => {
  if (!currentCodeType) return undefined;
  return getCategoryByType(currentCodeType)?.modalSize;
}, [currentCodeType]);

// 검색조건, 행 배치, 기본 모달 크기를 동일한 카테고리 설정에서 가져온다.
const currentCategory = useMemo(() => {
  if (!currentCodeType) return undefined;
  return getCategoryByType(currentCodeType);
}, [currentCodeType]);

// API와 검색 분기에 사용하는 원본 option.key/label은 유지하고 화면 표시용 번역만 추가한다.
const translatedSearchOptions = useMemo(() => (
  currentCategory?.searchOptions?.map((option) => {
    const optionCommentCd = getSearchOptionCommentCd(option.key);
    return {
      ...option,
      translatedLabel: optionCommentCd
        ? renderDescText(optionCommentCd, option.label)
        : option.label,
    };
  }) ?? []
), [currentCategory, renderDescText]);

// 검색조건은 가로 배치이므로 가장 긴 공통 너비가 아니라 항목별로 100~180px 범위에서 계산한다.
const searchOptionTitleWidths = useMemo(() => Object.fromEntries(
  translatedSearchOptions.map((option) => [
    option.key,
    getFormTitleWidth([option.translatedLabel], 100, 180),
  ]),
), [getFormTitleWidth, translatedSearchOptions]);


// 모달 생명주기 관리 훅 사용
useModalLifecycle(codeSearchModalProps || null, codeSearchModalVisible);
const moveFocusToNextCell = useCallback((config: FocusMoveConfig = {}) => {
  const { nextColumnKey, delay = 0 } = config;
  if (!nextColumnKey || !codeSearchModalProps?.gridRef?.current?.api) return;

  setTimeout(() => {
    const api = codeSearchModalProps.gridRef?.current?.api;
    if (!api) return;

    // 현재 선택된 행의 인덱스 찾기
    let targetRowIndex = -1;
    api.forEachNode((node: any) => {
      if (node.data?.valueIndex === codeSearchModalProps?.rowIndex && node.rowIndex !== null) {
        targetRowIndex = node.rowIndex;
      }
    });

    if (targetRowIndex !== -1) {
      api.startEditingCell({ rowIndex: targetRowIndex, colKey: nextColumnKey });
    }
  }, delay);
}, [codeSearchModalProps]);

	// 선택 이벤트 처리 함수 추가
  const handleSelection = useCallback(
    (selectedItem: SearchableDto, event?: SelectionEvent) => {
      if (!codeSearchModalProps?.onConfirm) return;

      const shouldMoveFocus = !event || event.type === "keyboard" || event.detail === 2;
      codeSearchModalProps.onConfirm(selectedItem, event);
      // 포커스 이동이 필요한 경우
      if (shouldMoveFocus && codeSearchModalProps.focusMoveConfig) {
        moveFocusToNextCell(codeSearchModalProps.focusMoveConfig);
      }
    },
    [codeSearchModalProps, moveFocusToNextCell]
  );
  
  	// 1. 검색 조건 변경 시 API 호출 (디바운싱 적용)
  useEffect(() => {
    if (!currentCodeType) return;
    const timeoutId = setTimeout(() => {
      executeSearch();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [currentCodeType, state.filters, executeSearch]);

 // 2. 그리드 데이터 변경 시 클라이언트 필터링
 useEffect(() => {
  applyClientFiltering();
}, [state.gridData, state.filters, applyClientFiltering]);

// 3. 모달 props 변경 시 초기화 (카테고리가 변경될 때만 실행)
useEffect(() => {
  if (codeSearchModalProps) {
    setPickerVisible(false);
    initializeSearch(setDateRanges);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [codeSearchModalProps?.category]);

  const handleFilterChange = useCallback(
    (key: string, value: string) => {
      dispatch({ type: "SET_FILTERS", payload: { ...state.filters, [key]: value } });
    },
    [state.filters]
  );

  const handleItemSelect = useCallback((item: SearchableDto | undefined) => {
    dispatch({ type: "SET_SELECTED", payload: item });
  }, []);

  const handleConfirmClick = useCallback(() => {
    if (!codeSearchModalProps) return;
    if (!state.selected) {
      alert("선택된 항목이 없습니다.");
      return;
    }
    handleSelection(state.selected);
    setCodeSearchModalVisible(false);
    if (codeSearchModalProps.onCloseConfirm) codeSearchModalProps.onCloseConfirm(state.selected);
  }, [state.selected, codeSearchModalProps, setCodeSearchModalVisible, handleSelection]);

  const renderSearchOptions = () => {
    if (!currentCodeType) return null;

    const searchOptions = translatedSearchOptions;
    if (!searchOptions || searchOptions.length === 0) return null;
		const searchOptionsPerRow = currentCategory?.searchOptionsPerRow ?? searchOptions.length;
		const hasMultipleSearchRows = searchOptions.length > searchOptionsPerRow;

    return (
      <div className={isKSK2022R01Style
		? "w-full shrink-0 px-[16px] pt-[14px]"
		: `w-full ${hasMultipleSearchRows ? "h-[20%]" : "h-[10%]"} flex items-center px-[8px]`}>
		<div className={isKSK2022R01Style
			? `flex w-full items-center gap-x-[8px] gap-y-[8px] rounded-[10px] border border-[#d8e3f0] bg-white p-[10px] shadow-sm ${hasMultipleSearchRows ? "flex-wrap" : "flex-nowrap"}`
			: "w-full flex flex-wrap items-center gap-x-[8px] gap-y-[4px]"}>
          {translatedSearchOptions.map((option, index) => {
						const shouldStartNewRow = index > 0 && index % searchOptionsPerRow === 0;
            const optionLabel = option.translatedLabel;
            if (option.type === "input") {
              return (
								<Fragment key={option.key}>
									{shouldStartNewRow && <div className="basis-full h-0" />}
									<InputForm
										title={optionLabel}
										titleWidth={searchOptionTitleWidths[option.key]}
										value={state.filters[option.key] || ""}
										editable={true}
										name={option.key}
										placeholder={option.placeholder}
										wrapperOption={option.wrapperOption ?? {
											wrapperStyle: isKSK2022R01Style ? "!h-[40px] flex-1 min-w-[280px]" : "flex-1 mb-[5px]",
											titleStyle: isKSK2022R01Style ? "flex h-[40px] w-[100px] min-w-[100px] items-center rounded-[8px] bg-[#e8eff8] px-[10px] text-left text-[14px] font-bold whitespace-nowrap text-[#183153]" : "w-[100px] min-w-[100px]",
											inputStyle: isKSK2022R01Style ? "flex-1 min-w-0 [&>input]:!h-[40px] [&>input]:!rounded-[8px] [&>input]:!border-[#c5d5e9] [&>input]:!px-[10px] [&>input]:text-[15px]" : "flex-1",
										}}
										onChange={(e) => handleFilterChange(option.key, e.target.value)}
										onKeyDown={(e) => e.key === "Enter" && executeSearch()}
									/>
								</Fragment>
              );
            }
            if (option.type === "dropdown") {
              const data = option.dataKey ? (commonCodeStore as any)[option.dataKey] ?? [] : [];
              return (
								<Fragment key={option.key}>
									{shouldStartNewRow && <div className="basis-full h-0" />}
									<DropDownForm
										title={optionLabel}
										titleWidth={searchOptionTitleWidths[option.key]}
										name={option.key}
										value={state.filters[option.key] || option.defaultValue || "all"}
										data={data}
										wrapperOption={option.wrapperOption ?? {
											wrapperStyle: isKSK2022R01Style ? "!h-[40px] min-w-[240px]" : "mb-[5px]",
											titleStyle: isKSK2022R01Style ? "flex h-[40px] w-[100px] min-w-[100px] items-center rounded-[8px] bg-[#e8eff8] px-[10px] text-left text-[14px] font-bold whitespace-nowrap text-[#183153]" : "w-[60px] min-w-[60px]",
											inputStyle: isKSK2022R01Style ? "w-[150px] [&>select]:!h-[40px] [&>select]:!rounded-[8px] [&>select]:!border-[#c5d5e9] [&>select]:!px-[8px] [&>select]:text-[15px]" : "w-[120px]",
										}}
										onChange={(e) => handleFilterChange(option.key, e.target.value)}
									/>
								</Fragment>
              );
            }
            if (option.type === 'dateRange') {
							return (
								<Fragment key={option.key}>
									{shouldStartNewRow && <div className="basis-full h-0" />}
									<SearchDateRangeForm
										label={optionLabel}
										titleWidth={searchOptionTitleWidths[option.key]}
										visible={pickerVisible}
										setVisible={setPickerVisible}
										dateRange={dateRanges[option.key]}
										setDateRange={(range) => handleDateRangeChange(option.key, range)}
										pickerPosition={pickerPosition}
										setPickerPosition={setPickerPosition}
									wrapperOption={{
										wrapperStyle: isKSK2022R01Style ? "!h-[40px] min-w-[320px]" : "mb-[5px]",
										titleStyle: isKSK2022R01Style
											? "form-title flex h-[40px] w-[100px] min-w-[100px] mr-[4px] items-center rounded-[8px] bg-[#e8eff8] px-[10px] text-left text-[14px] font-bold whitespace-nowrap text-[#183153]"
											: `form-title w-[100px] min-w-[100px] mr-[4px] ${defaultFormText}`,
										dateRangeStyle: isKSK2022R01Style
											? "border border-[#c5d5e9] text-center min-w-[220px] h-[40px] flex items-center justify-center rounded-[8px] px-[8px] bg-white whitespace-nowrap overflow-hidden text-ellipsis text-[15px]"
											: `border border-gray-300 text-center w-full h-[32px] flex items-center justify-center rounded-[4px] px-[4px] bg-white whitespace-nowrap overflow-hidden text-ellipsis ${defaultFormText}`,
									}}
									/>
								</Fragment>
							);
						}
            return null;
          })}

		  <div className="ml-auto flex shrink-0 items-center gap-2">
            <button
              onClick={() => {
                dispatch({ type: "RESET" });
                setPickerVisible(false);
                initializeSearch(
                  setDateRanges,
                  !codeSearchModalProps?.preserveExtraFilterOnReset,
                  true,
                );
              }}
			  className={isKSK2022R01Style
				? "h-[40px] min-w-[82px] rounded-[8px] border border-[#bdcadd] bg-[#eef2f7] px-[16px] text-[15px] font-bold text-[#27405f] shadow-sm hover:bg-[#e2e9f2]"
				: `${buttonFormHeightOption} text-white rounded-[4px] bg-gray-500 px-[16px] font-semibold hover:bg-gray-600`}
            >
              {renderDescText(DESC_BTN.RESET, "초기화")}
            </button>
            <button
              onClick={executeSearch}
			  className={isKSK2022R01Style
				? "h-[40px] min-w-[82px] rounded-[8px] bg-gradient-to-b from-[#2f67ec] to-[#1646b8] px-[16px] text-[15px] font-bold text-white shadow-md hover:brightness-105"
				: `${buttonFormHeightOption} text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold`}
            >
              {renderDescText(DESC_BTN.FETCH, "조회")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    if (!currentCodeType) return <div>지원하지 않는 카테고리입니다.</div>;

    return (
      <SearchTable
        codeType={currentCodeType}
        formName={codeSearchModalProps?.formName || ""}
        data={state.filteredList}
        selected={state.selected}
        setSelected={handleItemSelect}
        onConfirm={(item: any, event?: SelectionEvent) => handleSelection(item, event)}
        onCloseConfirm={codeSearchModalProps?.onCloseConfirm}
        processType_focus={codeSearchModalProps?.processType_focus || "normal"}
      />
    );
  };

  const renderErrorMessage = () => {
    if (!state.errorMessage) return null;
    return (
      <div className="px-4 py-2 bg-red-50 text-red-600 text-sm border border-red-200 rounded">
        {state.errorMessage}
      </div>
    );
  };

  const hasSearchOptions = currentCodeType
    ? (getCategoryByType(currentCodeType)?.searchOptions?.length ?? 0) > 0
    : false;
	const currentSearchOptionCount = currentCategory?.searchOptions?.length ?? 0;
	const currentSearchOptionsPerRow = currentCategory?.searchOptionsPerRow ?? currentSearchOptionCount;
	const hasMultipleSearchRows = currentSearchOptionsPerRow > 0
		&& currentSearchOptionCount > currentSearchOptionsPerRow;

	// 내부 검색 분기에는 원본 category를 사용하고, 팝업 제목에 표시할 category만 번역한다.
  const categoryCommentCd = getCategoryCommentCd(codeSearchModalProps?.category);
	const categoryLabel = categoryCommentCd
    ? renderDescText(categoryCommentCd, codeSearchModalProps?.category || "")
    : codeSearchModalProps?.category;
	const modalTitle = `${renderDescText(DESC_FLD.CODE_SEARCH, "코드 검색")} (${categoryLabel || ""} ${renderDescText(DESC_FLD.INFORMATION, "정보")})`;

	// 상·하단 버튼도 언어별 길이가 다르므로 최종 모달 너비 계산에 포함한다.
	const resetLabel = renderDescText(DESC_BTN.RESET, "초기화");
	const fetchLabel = renderDescText(DESC_BTN.FETCH, "조회");
	const clearLabel = renderDescText(DESC_BTN.CLEAR, "비우기");
	const confirmLabel = renderDescText(DESC_BTN.CONFIRM, "확인");
	const cancelLabel = renderDescText(DESC_BTN.CANCEL, "취소");

	// 카테고리 설정값은 한국어 화면 기준 최소 너비이며 번역문이 짧아도 이 값보다 줄이지 않는다.
	const configuredModalWidth = categoryModalSize?.width
		|| codeSearchModalProps?.wrapperOption?.defaultWidth
		|| 700;
	const modalSizeStorageKey = codeSearchModalProps?.wrapperOption?.sizeStorageKey
		|| `${codeSearchModalProps?.formName || 'common'}-${currentCodeType || codeSearchModalProps?.category || 'unknown'}-code-search-modal-size`;

	// 제목·검색행·하단 버튼 중 가장 넓은 영역을 기준으로 현재 언어의 모달 너비를 결정한다.
	const resolvedModalWidth = useMemo(() => {
		// getFormTitleWidth가 Form 내부 여백으로 더하는 24px을 제외하여 순수 문구 너비로 사용한다.
		const measureTextWidth = (text: string) => Math.max(
			0,
			getFormTitleWidth([text], 0, 2000) - 24,
		);

		// 팝업 헤더의 좌우 padding 24px씩을 포함한 제목 필요 너비다.
		const titleRequiredWidth = measureTextWidth(modalTitle) + 48;

		// 여러 줄 검색조건은 전체 합계가 아니라 각 행의 증가분 중 최댓값만 반영한다.
		const searchOptionsPerRow = currentCategory?.searchOptionsPerRow
			?? Math.max(translatedSearchOptions.length, 1);

		// 기본 너비가 한국어 기준이므로 초기화·조회 번역이 한국어보다 길어진 부분만 계산한다.
		const actionTextExpansion = measureTextWidth(`${resetLabel} ${fetchLabel}`)
			- measureTextWidth("초기화 조회");
		let searchRowExpansion = 0;

		for (let index = 0; index < translatedSearchOptions.length; index += searchOptionsPerRow) {
			const rowOptions = translatedSearchOptions.slice(index, index + searchOptionsPerRow);

			// 각 제목의 한국어 기준 100px 초과분을 현재 검색행의 확장값으로 합산한다.
			const titleExpansion = rowOptions.reduce(
				(total, option) => total + Math.max(0, (searchOptionTitleWidths[option.key] ?? 100) - 100),
				0,
			);

			// 초기화·조회 버튼은 검색조건 마지막 행에만 있으므로 마지막 행에만 증가분을 더한다.
			const isLastRow = index + searchOptionsPerRow >= translatedSearchOptions.length;
			searchRowExpansion = Math.max(
				searchRowExpansion,
				titleExpansion + (isLastRow ? Math.max(0, actionTextExpansion) : 0),
			);
		}

		// onClear가 있는 팝업만 비우기 버튼을 포함하여 실제 하단 버튼 구성과 맞춘다.
		const translatedFooterText = codeSearchModalProps?.onClear
			? `${clearLabel} ${confirmLabel} ${cancelLabel}`
			: `${confirmLabel} ${cancelLabel}`;
		const sourceFooterText = codeSearchModalProps?.onClear
			? "비우기 확인 취소"
			: "확인 취소";
		const footerExpansion = Math.max(
			0,
			measureTextWidth(translatedFooterText) - measureTextWidth(sourceFooterText),
		);

		// 단일 검색행은 줄바꿈이나 내부 스크롤 없이 표시되도록 실제 컨트롤 폭을 합산한다.
		const searchControlWidth = translatedSearchOptions.reduce((total, option) => {
			const titleWidth = searchOptionTitleWidths[option.key] ?? 100;
			const inputWidth = option.type === "input"
				? 230
				: option.type === "dateRange"
					? 230
					: 160;
			return total + titleWidth + inputWidth;
		}, 0);
		const searchActionWidth = Math.max(82, measureTextWidth(resetLabel) + 32)
			+ Math.max(82, measureTextWidth(fetchLabel) + 32)
			+ 8;
		const searchGapWidth = Math.max(0, translatedSearchOptions.length) * 8;
		const singleRowRequiredWidth = searchControlWidth
			+ searchActionWidth
			+ searchGapWidth
			+ 72;

		// 기본·제목·검색행·하단 버튼 필요 너비 중 최댓값을 사용하되 과도한 확장을 1800px로 제한한다.
		return Math.min(1800, Math.ceil(Math.max(
			configuredModalWidth,
			titleRequiredWidth,
			configuredModalWidth + searchRowExpansion,
			configuredModalWidth + footerExpansion,
			!hasMultipleSearchRows ? singleRowRequiredWidth : 0,
		)));
	}, [
		cancelLabel,
		clearLabel,
		codeSearchModalProps?.onClear,
		configuredModalWidth,
		confirmLabel,
		currentCategory?.searchOptionsPerRow,
		fetchLabel,
		getFormTitleWidth,
		hasMultipleSearchRows,
		modalTitle,
		resetLabel,
		searchOptionTitleWidths,
		translatedSearchOptions,
	]);

  return (
    <ModalWrapper
			size={categoryModalSize?.size || codeSearchModalProps?.wrapperOption?.size || 'md'}
			defaultWidth={resolvedModalWidth}
			defaultHeight={codeSearchModalProps?.wrapperOption?.defaultHeight}
			sizeStorageKey={modalSizeStorageKey}
			overlay={codeSearchModalProps?.wrapperOption?.overlay || true}
			onOverlayClick={() => setCodeSearchModalVisible(false)}
			onEscKeyDown={() => setCodeSearchModalVisible(false)}
		>
      <div className={isKSK2022R01Style ? "flex h-full flex-col overflow-hidden rounded-[6px] bg-[#f5f8fc]" : "contents"}>
      <div className={isKSK2022R01Style
		? "flex h-[58px] shrink-0 items-center gap-[10px] bg-gradient-to-r from-[#123f78] to-[#245d98] px-[22px] text-white"
		: "w-full h-[10%] flex items-center px-[24px] bg-gray-100"}>
		{isKSK2022R01Style && <SearchOutlined className="text-[20px]" />}
        <p
					className={isKSK2022R01Style
						? "min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[21px] font-bold"
						: `min-w-0 font-bold whitespace-nowrap overflow-hidden text-ellipsis ${defaultFormText}`}
					title={modalTitle}
				>
          {modalTitle}
        </p>
      </div>

      {renderSearchOptions()}

      {!hasSearchOptions && (
        <div className={isKSK2022R01Style ? "w-full shrink-0 px-[16px] pt-[14px]" : "w-full h-[10%] flex items-center px-[8px]"}>
		  <div className={isKSK2022R01Style ? "rounded-[10px] border border-[#d8e3f0] bg-white p-[10px] shadow-sm" : "contents"}>
          <SearchForm
            value={state.filters["search"] || ""}
            setSearch={(value) => handleFilterChange("search", value)}
            onSearch={() => {}}
          />
		  </div>
        </div>
      )}

      {renderErrorMessage()}

      <div className={isKSK2022R01Style
		? "mx-[16px] mb-[12px] mt-[10px] min-h-0 flex-1 overflow-hidden rounded-[10px] border border-[#d8e3f0] bg-white shadow-sm"
		: `w-full ${hasMultipleSearchRows ? "h-[60%]" : "h-[70%]"} px-[8px]`}>{renderTable()}</div>

      <div className={isKSK2022R01Style
		? "flex shrink-0 items-center justify-end gap-[8px] border-t border-[#d8e3f0] bg-white px-[18px] py-[12px]"
		: "w-full h-[10%] flex justify-end items-center gap-[8px] px-[8px]"}>
        {codeSearchModalProps?.onClear && (
          <button
			className={isKSK2022R01Style
				? "h-[40px] min-w-[86px] rounded-[8px] border border-[#bdcadd] bg-[#eef2f7] px-[16px] text-[15px] font-bold text-[#27405f] shadow-sm hover:bg-[#e2e9f2]"
				: `text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold ${defaultFormText}`}
            onClick={() => {
              codeSearchModalProps?.onClear?.();
              setCodeSearchModalVisible(false);
            }}
          >
            {renderDescText(DESC_BTN.CLEAR, "비우기")}
          </button>
        )}
        <button
		  className={isKSK2022R01Style
			? "h-[40px] min-w-[86px] rounded-[8px] bg-gradient-to-b from-[#2f67ec] to-[#1646b8] px-[16px] text-[15px] font-bold text-white shadow-md hover:brightness-105"
			: `text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold ${defaultFormText}`}
          onClick={handleConfirmClick}
        >
          {renderDescText(DESC_BTN.CONFIRM, "확인")}
        </button>
        <button
		  className={isKSK2022R01Style
			? "h-[40px] min-w-[86px] rounded-[8px] border border-[#bdcadd] bg-white px-[16px] text-[15px] font-bold text-[#52657d] shadow-sm hover:bg-[#eef2f7]"
			: `text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold ${defaultFormText}`}
          onClick={() => {
            codeSearchModalProps?.onClear?.();
            setCodeSearchModalVisible(false);
          }}
        >
          {renderDescText(DESC_BTN.CANCEL, "취소")}
        </button>
      </div>
	  </div>
    </ModalWrapper>
  );
};
